import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import type { EmailOtpType } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, Loader2, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react'

// How long we wait for a session to materialize before giving up. A PKCE
// `?code=` link is exchanged asynchronously by `detectSessionInUrl` using the
// device-local code_verifier; if the user opened the email on a different
// device/browser there is no verifier, the exchange never completes, and
// without a bound we would spin forever. 8s is comfortably longer than a real
// exchange yet short enough to surface an actionable error.
const VERIFY_TIMEOUT_MS = 8000

type Status = 'verifying' | 'ready' | 'error'

export interface SetPasswordFlowProps {
  /**
   * OTP type for verifier-free `token_hash` links (`verifyOtp`). `recovery` for
   * the password-reset flow, `invite` for the new-user invite flow. Used only as
   * a fallback when the link itself does not carry an explicit `type`.
   */
  linkType: Extract<EmailOtpType, 'recovery' | 'invite'>
  /** Header title shown in every state. */
  cardTitle: string
  /** Title shown above the password form. */
  formTitle: string
  /** Description shown above the password form. */
  formDescription: string
  /** Submit button label. */
  submitLabel: string
  /** Title shown on success. */
  successTitle: string
  /** Body shown on success. */
  successMessage: string
  /** Where to send the user after a successful password set. */
  redirectTo?: string
}

/**
 * Shared "establish a session from an auth email link, then set a password" UI.
 *
 * Backs both `/reset-password` (recovery) and `/auth/callback` (invite). It does
 * NOT depend on the `PASSWORD_RECOVERY` event — that never fires under the PKCE
 * flow this app uses. The load-bearing paths are `getSession()` (existing or
 * already-exchanged session), `verifyOtp()` (verifier-free `token_hash`, works
 * cross-device), and `detectSessionInUrl` exchanging a `?code=` in the
 * background (same-device only), observed via `onAuthStateChange`.
 */
export function SetPasswordFlow({
  linkType,
  cardTitle,
  formTitle,
  formDescription,
  submitLabel,
  successTitle,
  successMessage,
  // `/login` exists in BOTH the admin and workspace shells (the admin shell has
  // no `/dashboard` route — that default would land on NotFound). LoginForm's
  // authenticated-user effect then routes onward per shell/role.
  redirectTo = '/login',
}: SetPasswordFlowProps) {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [status, setStatus] = useState<Status>('verifying')
  const [linkError, setLinkError] = useState('')

  useEffect(() => {
    let cancelled = false
    // Supabase puts OTP errors (e.g. an expired link) in the URL hash, and the
    // token material may arrive as either a query param or a hash fragment.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const query = new URLSearchParams(window.location.search)

    const fail = (message: string) => {
      if (cancelled) return
      setLinkError(message)
      setStatus('error')
    }

    // 1. Explicit error handed back by Supabase (most commonly otp_expired).
    const errorCode = hash.get('error_code') || query.get('error_code')
    const errorDescription =
      hash.get('error_description') || query.get('error_description')
    const errorParam = hash.get('error') || query.get('error')
    if (errorParam || errorCode) {
      if (errorCode === 'otp_expired') {
        fail(
          'This link has expired. Request a new one and open it within the hour.',
        )
      } else {
        fail(
          errorDescription?.replace(/\+/g, ' ') ||
            'This link is invalid or has already been used. Request a new one.',
        )
      }
      return
    }

    const tokenHash = query.get('token_hash') || hash.get('token_hash')
    const typeParam = (query.get('type') || hash.get('type')) as EmailOtpType | null

    async function establish() {
      // 2. A verifier-free `token_hash` link is authoritative and MUST be
      // verified BEFORE trusting any pre-existing session. The `.icrop.net`
      // cookie is shared across every subdomain, so being logged in anywhere
      // means a session is already present here — if we short-circuited on that
      // session, the link's token would never be consumed and a later
      // `updateUser({ password })` would change the password on the logged-in
      // account instead of the link's owner (e.g. an admin verifying an
      // invitee's link would silently reset their OWN password).
      if (tokenHash) {
        // Drop any pre-existing session first so `verifyOtp` establishes the
        // link owner's session cleanly and the password can only apply to them.
        const {
          data: { session: existing },
        } = await supabase.auth.getSession()
        if (cancelled) return
        if (existing) {
          // Best-effort, LOCAL-scope drop of the stale session. `scope: 'local'`
          // only clears this device's session (a global signOut would log the
          // acting admin out of every other subdomain/tab). `verifyOtp` below
          // re-establishes the link owner's session regardless, so a transient
          // signOut failure must never abort verification — swallow it and
          // proceed rather than stranding the user on the 8s timeout.
          try {
            await supabase.auth.signOut({ scope: 'local' })
          } catch (signOutErr) {
            console.warn(
              'SetPasswordFlow: pre-verify signOut failed, proceeding to verify',
              signOutErr,
            )
          }
          if (cancelled) return
        }

        // `token_hash` link → verify it WITHOUT a code_verifier. This is the
        // only path that works when the link is opened on another device/browser.
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: typeParam || linkType,
          token_hash: tokenHash,
        })
        if (cancelled) return
        if (verifyError) {
          fail(
            'This link is invalid or has expired. Request a new one and open it within the hour.',
          )
          return
        }
        setStatus('ready')
        return
      }

      // 3. No `token_hash`: a session may already exist — the user refreshed
      // this page, or `detectSessionInUrl` already consumed a
      // `#access_token`/`?code=`.
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (cancelled) return
      if (session) {
        setStatus('ready')
        return
      }

      // 4. Otherwise this is a PKCE `?code=` link: `detectSessionInUrl` is
      // exchanging it in the background using the device-local verifier. We
      // cannot force it here — wait for `onAuthStateChange` or the timeout.
    }

    // `establish()` runs fire-and-forget, but an unguarded reject (e.g. a
    // transient getSession()/verifyOtp() network throw) would otherwise be an
    // unhandled promise rejection AND leave the user waiting out the full
    // VERIFY_TIMEOUT_MS only to see a misleading cross-device message. Surface
    // an actionable error immediately instead.
    void establish().catch((establishErr) => {
      if (cancelled) return
      console.error('SetPasswordFlow establish() failed:', establishErr)
      fail('We could not verify your link. Please request a new one and try again.')
    })

    // Catches the async `?code=` exchange and any late session arrival.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && !cancelled) setStatus('ready')
    })

    const timer = setTimeout(() => {
      if (cancelled) return
      setStatus((current) => {
        if (current !== 'verifying') return current
        setLinkError(
          'We could not verify your link. If you opened it on a different device or browser than the one you requested it from, open it there instead — or request a new link.',
        )
        return 'error'
      })
    }, VERIFY_TIMEOUT_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
      subscription.unsubscribe()
    }
  }, [linkType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })

      if (updateError) {
        throw new Error(updateError.message)
      }

      setSuccess(true)
      setTimeout(() => navigate(redirectTo, { replace: true }), 2000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password'
      setError(errorMessage)
    }

    setLoading(false)
  }

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <FileText className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-2xl">{cardTitle}</CardTitle>
            <CardDescription>Verifying your link...</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              This should only take a moment.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-2xl">{cardTitle}</CardTitle>
            <CardDescription>We couldn't verify your link</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">{linkError}</p>
            <Link to="/forgot-password" className="block">
              <Button className="w-full">Request a new link</Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <FileText className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">{success ? successTitle : formTitle}</CardTitle>
          <CardDescription>
            {success ? 'Redirecting you now...' : formDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
              <p className="text-sm text-muted-foreground">{successMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  New Password
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  submitLabel
                )}
              </Button>

              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
